/**
 * API 봉투 공유 타입 (DESIGN §7.1).
 *
 * 백엔드의 `evca/api/deps.py::Meta` 와 **한 글자도 어긋나면 안 된다**.
 * `meta.coverage` 누락은 계약 위반이므로 여기서도 필수 필드로 둔다 —
 * 타입이 옵션이면 프론트가 조용히 커버리지 없는 숫자를 그릴 수 있다.
 */

export type Mode = 'strict' | 'standard' | 'user_facing'

export const MODES: readonly Mode[] = ['strict', 'standard', 'user_facing'] as const

export const MODE_LABEL: Record<Mode, string> = {
  strict: '설비 고장만',
  standard: '통신 포함',
  user_facing: '이용자 관점',
}

export const MODE_HINT: Record<Mode, string> = {
  strict: 'DOWN = 운영중지·점검중. 사업자 연계 문제를 고장으로 세지 않는다',
  standard: 'DOWN = 통신이상·운영중지·점검중 (기본)',
  user_facing: 'standard + 이용 제한(limitYn=Y). 실측 55%가 제한 상태라 값이 크게 달라진다',
}

export interface Meta {
  mode: Mode
  from: string | null
  to: string | null
  /** 모집단 폭포의 **최종 N** — 응답별 건수가 아니다 (§3.6) */
  population_n: number
  /** 관측 커버리지. 이 값 없이 가동률을 표시하는 것은 계약 위반이다 */
  coverage: number
  data_version: string
  generated_at: string
  n_excluded_low_coverage: number
  representation: string | null
  pruned: boolean
  notes: string[]
}

export interface Envelope<T> {
  data: T
  meta: Meta
}

export interface ApiError {
  error: { code: string; message_ko: string; hint: string }
}

/* ── 엔드포인트별 data 타입 ─────────────────────────────────────── */

export interface SummaryData {
  n_chargers: number
  t_up_min: number
  t_down_min: number
  t_na_min: number
  t_obs_min: number
  t_total_min: number
  availability: number | null
  availability_ci: [number, number]
  utilization: number | null
  snapshot_note: string
}

export interface WaterfallStep {
  step_index: number
  label: string
  kind: 'start' | 'exclude' | 'split'
  n_affected: number
  n_remaining: number
  reason: string | null
}

/** §3.7.1 — '제외'와 '판정 보류'는 다른 진술이다 */
export type Verdict = 'comparable' | 'excluded' | 'insufficient_window'

export const VERDICT_LABEL: Record<Verdict, string> = {
  comparable: '비교가능',
  excluded: '제외',
  insufficient_window: '판정 보류',
}

export interface OperatorQuality {
  busi_id: string
  busi_nm: string | null
  n_chargers: number
  na_rate: number
  stale_rate: number
  upddt_error_rate: number
  distinct_codes: number
  uses_code_2: boolean
  uses_down_codes: boolean
  window_days: number
  quality: number
  verdict: Verdict
  comparable: boolean
  reasons: string | null
}

export interface CalendarDay {
  /** KST 일자 — 다른 일 집계와 같은 기준 (§3.5 원칙 ④) */
  date_kst: string
  op: string
  n_polls: number
  n_success: number
  n_partial: number
  n_failed: number
  n_blocked: number
  n_skipped: number
  calls: number
  received: number
}

export interface OpsGap {
  op: string
  start_ts: string
  end_ts: string
  minutes: number
  cause: string
}

export interface StatMixRow {
  date_utc: string
  stat: string
  minutes: number
}

export interface StaleBin {
  log10_min: number
  n: number
}

export interface NoteCategory {
  category: string
  n_chargers: number
  n_limit_y: number
  limit_share: number
  keywords: string
  sample_note: string | null
}

export interface RankingItem {
  entity_id: string
  n_chargers: number
  availability: number | null
  availability_ci: [number, number]
  /** 이용률 = utilization_min / t_obs. **CI 가 없다** — 점유 시간의 비율이라
   *  이항 시행이 정의되지 않는다 (DESIGN §8.9) */
  utilization?: number | null
  coverage: number
  t_obs_min: number
  comparable: boolean | null
  verdict: Verdict | null
  quality: number | null
  quality_reasons: string | null
}

export interface RankingData {
  entity: string
  ranking: RankingItem[]
  excluded: RankingItem[]
}

/** 가동률 구간 하나 — [from, to) */
export interface DistributionBin {
  from: number
  to: number
  n: number
}

/** 충전기 단위 가동률 분포 — 전수 구간 빈도 (DESIGN §7.2) */
export interface DistributionData {
  /** 커버리지 기준을 넘긴 충전기 수(전수) = perfect + partial + dead.
   *  기준 미달 수는 meta.n_excluded_low_coverage */
  n: number
  /** 가동률 정확히 1 */
  perfect: number
  /** 0 과 1 사이 — bins 의 합 */
  partial: number
  /** 가동률 정확히 0 */
  dead: number
  /** [from, to) 5%p 20칸. 정확히 0·정확히 1 은 빠진다 */
  bins: DistributionBin[]
}

export interface TimeseriesPoint {
  date: string
  n_chargers: number
  t_up_min: number
  t_down_min: number
  t_na_min: number
  availability: number | null
  coverage: number | null
  utilization: number | null
}

export interface TimeseriesData {
  points: TimeseriesPoint[]
  missing_dates: string[]
}

/** 연식 코호트 판정 (§8.10.1). `verdict` 는 셋 중 하나다 */
export type CohortVerdict = 'cohort' | 'single_operator' | 'thin'

export interface YearCohort {
  year: number
  n_chargers: number
  n_busi: number
  top_busi: string | null
  top_share: number
  verdict: CohortVerdict
  /** 왜 별도 관리인가. `verdict === 'cohort'` 면 빈 문자열 */
  reason: string
}

export interface YearCohortsData {
  cohorts: YearCohort[]
  n_usable: number
  single_operator_share: number
}

/** 비교 축 — `/metrics/ranking?entity=` 가 받는 값 */
export const RANK_ENTITIES = [
  { key: 'busi', label: '사업자' },
  { key: 'zcode', label: '지역' },
  { key: 'maker', label: '제조사' },
  { key: 'chger_type', label: '충전기 타입' },
  { key: 'kind', label: '설치 환경' },
  { key: 'year', label: '설치 연식' },
  { key: 'floor_type', label: '설치 층' },
] as const

export type RankEntity = (typeof RANK_ENTITIES)[number]['key']

/* ── 지도 (§8.2) ─────────────────────────────────────────────── */

/** `/geo/h3` — **열 지향**. 셀 2만 개를 객체 배열로 받으면 파싱이 병목이 된다 */
export interface H3Data {
  /** 이용률. 지도가 사용량 화면으로도 성립하게 하는 지표 (§8.0) */
  utilization?: (number | null)[]
  cells: string[]
  lat: number[]
  lng: number[]
  n_chargers: number[]
  availability: (number | null)[]
  /** 셀별 관측 커버리지 — 지도에서 가장 잘 퍼지는 지표다 */
  coverage: (number | null)[]
  /** 셀의 대표 시·도 (최빈). 두 시도가 섞인 셀 수는 n_cells_mixed_zcode */
  zcode?: (string | null)[]
  n_cells_mixed_zcode?: number
}

/** `/geo/points` — 상한 초과 시 서버가 H3Data 를 대신 준다 (meta.representation) */
export interface PointsData {
  keys: string[]
  lat: number[]
  lng: number[]
  availability: (number | null)[]
  coverage: (number | null)[]
  /** 이용률 — H3 응답과 같은 이름·같은 정의 (§8.9) */
  utilization?: (number | null)[]
  zcode?: (string | null)[]
}

/** `/meta/client` — 빌드가 아니라 **지금** 판정한 런타임 상태 (§8.2.1 규칙 3) */
export interface ClientConfig {
  features: Record<string, boolean>
  downgrades: string[]
  missing_reference_files: { path: string; blocks: string }[]
  /** 빈 문자열이면 외부 타일을 요청하지 않는다 (§8.2.2) */
  basemap_style_url: string
  data_version: string
}

export interface ManifestData {
  data_version: string
  generated_at: string
  build_seconds: number
  population_n: number
  runtime_provisional: boolean
  gates: Record<string, unknown>
  features: Record<string, boolean>
  modes: Mode[]
  row_counts: Record<string, number>
  warnings: string[]
  population_waterfall: {
    label: string
    kind: string
    n_affected: number
    n_remaining: number
    reason: string
  }[]
  input_range: {
    first_observed_at: string | null
    last_observed_at: string | null
    n_observations: number
    n_chargers_observed: number
    silver_files: number
  }
}

/* ── stat 코드 표시 (공통코드 3.2) ──────────────────────────────── */

export const STAT_LABEL: Record<string, string> = {
  '0': '알수없음',
  '1': '통신이상',
  '2': '충전대기',
  '3': '충전중',
  '4': '운영중지',
  '5': '점검중',
  '6': '예약중',
  '9': '상태미확인',
  NA: 'STALE 재분류',
}

export type StatGroup = 'UP' | 'DOWN' | 'NA'

/** §3.1 — 모드별 그룹. 백엔드 `dim_stat` 과 같은 규칙이다. */
export function statGroup(code: string, mode: Mode): StatGroup {
  if (['2', '3', '6'].includes(code)) return 'UP'
  const down = mode === 'strict' ? ['4', '5'] : ['1', '4', '5']
  if (down.includes(code)) return 'DOWN'
  return 'NA'
}

/* ── 노후화 분석 (DESIGN §8.11) ───────────────────────────────────── */

/** 연식 1년당 발생률 배수 — 통제 전(raw)·후(stratified)를 **같은 셀**에서 잰 값 */
export interface AgingEffect {
  mode: Mode
  outcome: 'down_events' | 'na_share'
  spec: 'raw' | 'stratified'
  irr: number
  lo: number
  hi: number
  n_cells: number
  n_strata: number
  n_chargers: number
  note: string
}

export interface AgingCohortRow {
  year: number
  usable: boolean
  n_chargers: number
  events: number
  events_per_1000h: number | null
  na_share: number | null
  utilization: number | null
}

export interface RemovalRow {
  year: number
  usable: boolean
  n_seen: number
  removed: number
  removed_rate: number | null
  flagged_del: number
  del_flag_ever: number
  attr_changed: number
  new_after_start: number
  top_busi: string | null
  top_busi_share: number | null
  dominated: boolean
}

export interface RemovalBusi {
  busi_id: string
  busi_nm: string
  n_seen: number
  removed: number
  removed_year_median: number | null
}

export interface AgingPublicData {
  effects: AgingEffect[]
  cohorts: Partial<Record<Mode, AgingCohortRow[]>>
  removal: RemovalRow[]
  removal_busi: RemovalBusi[]
  snapshots: {
    n: number
    first: string | null
    last: string | null
    absent_runs: number
    series: { snap: string; n_chargers: number }[]
  }
  window_days: number | null
  dominant_share: number
}

export interface KecoMeta {
  charger_info_file: string
  alarm_from: string | null
  alarm_to: string | null
  charge_from: string | null
  charge_to: string | null
  n_alarm: number
  n_episode: number
  n_session: number
  n_charger_info: number
  policy_break_ym: string
  definitions: string
  generated_at: string
}

export interface KecoAgeCurveRow {
  age: number
  period: 'pre' | 'post'
  chg_fail: number
  down_h: number
  sessions: number
  mwh: number
  charger_months: number
  n_cohorts: number
  ym_from: string
  ym_to: string
  fail_per_chg_month: number | null
  down_h_per_chg_month: number | null
}

export interface KecoSurvival {
  cohort: number
  n: number
  deleted: number
  curve: { age: number; at_risk: number; events: number; survival: number }[]
}

export interface KecoPowerRow {
  ym: string
  cohort: number
  rated_kw: number
  kw_n: number
  kw_p50: number | null
  kw_p90: number | null
  achievement: number | null
}

export interface KecoStressBin {
  bin: number
  n: number
  mwh_pm_lo: number
  mwh_pm_hi: number
  mwh_pm_med: number
  charger_months: number
  chg_fail: number
  age_mean: number
  share_200kw: number
  fail_per_chg_month: number | null
}

/** 환경공단 집계가 아직 없으면 `available=false` 와 사유만 온다 (§4.7) */
export type KecoData<T> = ({ available: true; meta: KecoMeta } & T) | { available: false; reason: string }

export type AgingKecoData = KecoData<{
  age_curve: KecoAgeCurveRow[]
  survival: KecoSurvival[]
  power: KecoPowerRow[]
  stress: KecoStressBin[]
  n_chargers: number
  n_cohort: number
  power_min_sessions: number
}>

export interface UsagePowerRow {
  ym: string
  rated_kw: number
  sessions: number
  mwh: number
  kw_n: number
  kw_p50: number | null
  kw_p90: number | null
  achievement: number | null
  kwh_p50: number | null
  dur_min_p50: number | null
  zero_kwh: number
  zero_kwh_share: number | null
  kwh_overflow: number
}

export type UsagePowerData = KecoData<{ rows: UsagePowerRow[] }>
